import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Select, {
  CSSObjectWithLabel,
  GroupBase,
  StylesConfig,
} from "react-select";
import { useTranslation } from "react-i18next";
import axiosClient from "../../libs/axiosClient";
import HeaderMain from "../../components/HeaderMain/HeaderMain";
import HeaderMobile from '../../components/HeaderMobile/HeaderMobile';
import PaymentCancelModal from "../../components/PaymentCancelModal/PaymentCancelModal";
import Footer from "../../components/Footer/Footer";
import styles from "./MyPage.module.scss";
import ukFlag from "../../assets/hospitalReservation/ukFlag.png";
import chinaFlag from "../../assets/hospitalReservation/chinaFlag.png";
import japanFlag from "../../assets/hospitalReservation/japanFlag.png";
import arabFlag from "../../assets/hospitalReservation/arabFlag.png";
import kakaoIcon from "../../assets/hospitalReservation/kakaotalkIcon.png";
import whatsappIcon from "../../assets/hospitalReservation/whatsappIcon.png";
import lineIcon from "../../assets/hospitalReservation/lineIcon.png";
import wechatIcon from "../../assets/hospitalReservation/wechatIcon.png";

const customStyles: StylesConfig<
  {
    value: string;
    label: JSX.Element;
  },
  false,
  GroupBase<{ value: string; label: JSX.Element }>
> = {
  control: (provided: CSSObjectWithLabel) => ({
    ...provided,
    display: "flex",
    alignItems: "center",
    height: "40px",
    padding: "5px",
    border: "1px solid #ccc",
    borderRadius: "5px",
    width: "100%",
    boxSizing: "border-box",
    overflow: "hidden",
  }),
  valueContainer: (provided: CSSObjectWithLabel) => ({
    ...provided,
    display: "flex",
    alignItems: "center",
    justifyContent: "start",
    height: "100%",
    padding: "0 8px",
  }),
  placeholder: (provided: CSSObjectWithLabel) => ({
    ...provided,
    display: "flex",
    alignItems: "center",
    justifyContent: "start",
    height: "100%",
    color: "#aaa",
    fontSize: "14px",
  }),
  singleValue: (provided: CSSObjectWithLabel) => ({
    ...provided,
    display: "flex",
    alignItems: "center",
    justifyContent: "start",
    gap: "8px",
    fontSize: "14px",
    img: {
      width: "20px",
      height: "20px",
      objectFit: "cover",
      marginRight: "7px",
    },
  }),
  menu: (provided: CSSObjectWithLabel) => ({
    ...provided,
    zIndex: 5,
    padding: "5px 0",
  }),
  option: (provided: CSSObjectWithLabel, state) => ({
    ...provided,
    display: "flex",
    alignItems: "center",
    backgroundColor: state.isFocused ? "#f0f0f0" : "white",
    color: "#333",
    padding: "8px 12px",
    gap: "10px",
    fontSize: "14px",
    img: {
      width: "30px",
      height: "30px",
      objectFit: "cover",
      marginRight: "10px",
    },
  }),
  dropdownIndicator: (provided: CSSObjectWithLabel) => ({
    ...provided,
    padding: "5px",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
};

function MyPage() {
  const [activeTab, setActiveTab] = useState(0);
  const [userData, setUserData] = useState<any>(null);
  const [orderData, setOrderData] = useState<any>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const [isMobile, setIsMobile] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const { t, i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const tab = parseInt(query.get("tab") || "0", 10);
    setActiveTab(tab);
  }, [location.search]);
  
  useEffect(() => {
    const savedLanguage = localStorage.getItem("selectedLanguage") || "en";
    if (i18n.language !== savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }
    
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [i18n]);
  
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axiosClient.get("/api/v1/users/mypage",
          {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
        setUserData(response.data);
        setFormData(response.data); 
      } catch (error) {
        console.error("Failed to fetch user data:", error);
      }
    };

    fetchUserData();
  }, []);
  
  useEffect(() => {
    if (activeTab === 1) {
      const fetchOrderData = async () => {
        try {
          const response = await axiosClient.get("api/v1/order", {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          });
          setOrderData(response.data);
          console.log("주문 내역 데이터:", response.data);
        } catch (error) {
          console.error("Failed to fetch order data:", error);
        }
      };
      fetchOrderData();
    }
  }, [activeTab]);
  
  const [previousData, setPreviousData] = useState<any>(null);
  
  const handleEditToggle = async () => {
    if (isEditing) {
      try {
        await axiosClient.put("/api/v1/users/mypage", formData);
        alert(t("myPage.updateSuccess"));
        setUserData(formData);
        setIsEditing(false);
      } catch (error) {
        console.error("Failed to update user data:", error);
        alert(t("myPage.updateError"));
      }
    } else {
      setPreviousData(formData);
      setIsEditing(true);
    }
  };
  
  const handleCancelEdit = () => {
    setFormData(previousData);
    setIsEditing(false);
  };
  
  const handleInputChange = (key: string, value: any) => {
    setFormData({ ...formData, [key]: value });
  };
  
  const handleCancelClick = (orderId: number) => {
    setSelectedOrderId(String(orderId));
    setIsCancelModalOpen(true);
  };

  const handleCancelPayment = async () => {
    if (!selectedOrderId) return;
  
    try {
      const response = await axiosClient.post(
        "api/v1/payment/cancel",
        { orderId: String(selectedOrderId) },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
  
      console.log("결제 취소 성공:", response.data);
  
      setIsCancelModalOpen(false);
  
    } catch (error) {
      alert(t("paymentCancelModal.error"));
    }
  };

  const nationalityOptions = [
    {
      value: "uk",
      label: (
        <div className={styles.option}>
          <img src={ukFlag} alt="uk Flag" className={styles.icon} />
          English
        </div>
      ),
    },
    {
      value: "japan",
      label: (
        <div className={styles.option}>
          <img src={japanFlag} alt="Japan Falg" className={styles.icon} />
          日本
        </div>
      ),
    },
    {
      value: "china",
      label: (
        <div className={styles.option}>
          <img src={chinaFlag} alt="China Flag" className={styles.icon} />
          中国
        </div>
      ),
    },
    {
      value: "arab",
      label: (
        <div className={styles.option}>
          <img src={arabFlag} alt="Arab Flag" className={styles.icon} />
          عربي
        </div>
      ),
    },
  ];
  
  const messengerOptions = [
    {
      value: "kakao",
      label: (
        <div className={styles.option}>
          <img src={kakaoIcon} alt="KakaoTalk" className={styles.icon} />
          KakaoTalk
        </div>
      ),
    },
    {
      value: "line",
      label: (
        <div className={styles.option}>
          <img src={lineIcon} alt="Line" className={styles.icon} />
          Line
        </div>
      ),
    },
    {
      value: "wechat",
      label: (
        <div className={styles.option}>
          <img src={wechatIcon} alt="WeChat" className={styles.icon} />
          WeChat
        </div>
      ),
    },
    {
      value: "whatsapp",
      label: (
        <div className={styles.option}>
          <img src={whatsappIcon} alt="WhatsApp" className={styles.icon} />
          WhatsApp
        </div>
      ),
    },
  ];
  
  if (!userData) {
    return <div>Loading...</div>;
  }

  return (
    <>
      {isMobile ? <HeaderMobile /> : <HeaderMain selectedTab="myPage" onTabChange={() => {}} />}
      <div className={styles.container}>
        {isMobile ? (
          <nav className={styles.mobileNav}>
            <ul className={styles.mobileCategoryList}>
              <li className={activeTab === 0 ? styles.active : ""} onClick={() => setActiveTab(0)}>
                {t("myPage.title")}
              </li>
              <li className={activeTab === 1 ? styles.active : ""} onClick={() => setActiveTab(1)}>
                {t("myPage.paymentHistory")}
              </li>
              <li className={activeTab === 2 ? styles.active : ""} onClick={() => setActiveTab(2)}>
                {t("myPage.cancelHistory")}
              </li>
            </ul>
          </nav>
        ) : (
          <aside className={styles.sidebar}>
            <h2>{t("myPage.page")}</h2>
            <ul className={styles.categoryList}>
              <li className={activeTab === 0 ? styles.active : ""} onClick={() => setActiveTab(0)}>
                {t("myPage.title")}
              </li>
              <li className={activeTab === 1 ? styles.active : ""} onClick={() => setActiveTab(1)}>
                {t("myPage.paymentHistory")}
              </li>
              <li className={activeTab === 2 ? styles.active : ""} onClick={() => setActiveTab(2)}>
                {t("myPage.cancelHistory")}
              </li>
            </ul>
          </aside>
        )}
        <div className={styles.content}>
          {activeTab === 0 ? (
            <>
              <div className={styles.header}>
                <h2 className={styles.title}>{t('myPage.title')}</h2>
                <h3 className={styles.subtitle}>{t('myPage.subtitle')}</h3>
              </div>
              <div className={styles.form}>
                <div className={styles.formGroup}>
                <label>{t("myPage.id")}</label>
                <span className={styles.textDisplay}>{formData.uid || "-"}</span> 
                </div>
                
                <div className={styles.separator}></div>
                
                <div className={styles.formGroup}>
                  <label>{t('myPage.email')}</label>
                  {isEditing ? (
                    <input 
                      type="text"
                      value={formData.email || ""}
                      onChange={(e) => handleInputChange("email", e.target.value)}
                    />
                  ) : (
                    <span className={styles.textDisplay}>{formData.email || "-"}</span>
                  )}
                </div>

                <div className={styles.separator}></div>

                <div className={styles.formGroup}>
                  <label>{t('myPage.passportName')}</label>
                  {isEditing ? (
                    <input 
                      type="text"
                      value={formData.first_name + " " + formData.last_name || ""}
                      onChange={(e) => handleInputChange("name", e.target.value)}
                    />
                  ) : (
                    <span className={styles.textDisplay}>{formData.first_name + " " + formData.last_name || "-"}</span>
                  )}
                </div>

                <div className={styles.separator}></div>

                <div className={styles.customFormGroup}>
                  <label>{t('myPage.country')}</label>
                  {isEditing ? (
                    <Select
                      options={nationalityOptions}
                      styles={customStyles}
                      classNamePrefix="react-select"
                      isDisabled={!isEditing}
                      value={nationalityOptions.find((option) => option.value === formData.country?.toLowerCase())}
                      onChange={(option) => handleInputChange("country", option?.value.toUpperCase())}
                    />
                  ) : (
                    <span className={styles.textDisplay}>
                      {nationalityOptions.find((option) => option.value.toUpperCase() === formData.country)?.label && (
                        <div className={styles.inlineDisplay}>
                          <img src={nationalityOptions.find((option) => option.value.toUpperCase() === formData.country)?.label.props.children[0].props.src} 
                            alt={formData.country} className={styles.iconSmall} />
                          {nationalityOptions.find((option) => option.value.toUpperCase() === formData.country)?.label.props.children[1]}
                        </div>
                      )}
                    </span>
                  )}
                </div>

                <div className={styles.separator}></div>

                <div className={styles.formGroup}>
                  <label>{t('myPage.phone')}</label>
                  {isEditing ? (
                    <input 
                      type="text"
                      value={formData.phone || ""}
                      onChange={(e) => handleInputChange("phone", e.target.value)}
                    />
                  ) : (
                    <span className={styles.textDisplay}>{formData.phone || "-"}</span>
                  )}
                </div>

                <div className={styles.separator}></div>

                <div className={styles.customFormGroup}>
                  <label>{t('myPage.messenger')}</label>
                  {isEditing ? (
                    <Select
                      options={messengerOptions}
                      styles={customStyles}
                      isDisabled={!isEditing}
                      value={messengerOptions.find((option) => option.value === formData.messenger)}
                      onChange={(option) => handleInputChange("messenger", option?.value || null)}
                    />
                  ) : (
                    <span className={styles.textDisplay}>
                      {messengerOptions.find((option) => option.value === formData.messenger)?.label && (
                        <div className={styles.inlineDisplay}>
                          <img src={messengerOptions.find((option) => option.value === formData.messenger)?.label.props.children[0].props.src} 
                            alt={formData.messenger} className={styles.iconSmall} />
                          {messengerOptions.find((option) => option.value === formData.messenger)?.label.props.children[1]}
                        </div>
                      )}
                    </span>
                  )}
                </div>

                <div className={styles.separator}></div>

                <div className={styles.formGroup}>
                  <label>{t('myPage.messengerId')}</label>
                  {isEditing ? (
                    <input 
                      type="text"
                      value={formData.messenger_id || ""}
                      onChange={(e) => handleInputChange("messenger_id", e.target.value)}
                    />
                  ) : (
                    <span className={styles.textDisplay}>
                      {formData.messenger_id || "-"}
                    </span>
                  )}
                </div>
                
                <div className={styles.buttonContainer}>
                  {isEditing ? (
                    <>
                      <button className={styles.cancelButton} onClick={handleCancelEdit}>
                        {t("myPage.cancelEdit")}
                      </button>
                      <button className={styles.saveButton} onClick={handleEditToggle}>
                        {t("myPage.finishEdit")}
                      </button>
                    </>
                  ) : (
                    <button className={styles.editButton} onClick={handleEditToggle}>
                      {t("myPage.edit")}
                    </button>
                  )}
                </div>
              </div>
            </>
          ) :  (
            <>
              <h2 className={styles.paymentTitle}>
                {t('myPage.paymentHistory')}
              </h2>
              <div className={styles.orderList}>
                {orderData && orderData.length > 0 ? (
                  orderData.map((order: any) => (
                    <div key={order.id} className={styles.orderCard}>
                      <div className={styles.orderHeader}>
                        <span className={styles.orderNum}>
                          {t("myPage.orderNumber", { orderNum: order.order_num })}
                        </span>
                        <div className={styles.statusContainer}>
                          <span className={`${styles.orderStatus} ${order.status === "success" ? styles.success : styles.failed}`}>
                            {order.status === "success" ? t("myPage.paymentCompleted") : "Failed"}
                          </span>
                        </div>
                      </div>
                      <div className={styles.orderBody}>
                        <img
                          src={order.hospital_profile_image[0] || "/default-thumbnail.png"}
                          alt="병원 이미지"
                          className={styles.thumbnail}
                        />
                        <div className={styles.orderInfo}>
                          {order.order_products.map((product: any, index: number) => (
                            <div key={index} className={styles.productContainer}>
                              <p className={styles.productTitle}>{product.product_title}</p>
                              <p className={styles.optionTitle}>{product.option_title}</p>
                            </div>
                          ))}
                          <p className={styles.hospitalName}>{order.order_products[0]?.hospital_name}</p>
                        </div>
                        <div className={styles.paymentContainer}>
                        <span className={styles.price}>
                          {order.currency_code === "USD" 
                            ? Number(order.total_price).toFixed(2) 
                            : order.total_price} {order.currency_code}
                        </span>
                          <span className={styles.paymentMethod}>
                            {order.payment_method.toUpperCase()}
                          </span>
                          <span className={styles.orderDate}>
                            {new Date(order.created_at).toLocaleDateString()}
                          </span>
                          <button 
                            className={styles.cancelButton}
                            onClick={() => handleCancelClick(order.order_num)}>
                            {t("myPage.cancel")}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                ) : activeTab === 2 ? (
                  <>
                    <h2 className={styles.paymentTitle}>{t("myPage.cancelHistory")}</h2>
                    <div className={styles.orderList}>
                      <p>{t("myPage.noCancelHistory")}</p>
                    </div>
                  </>
                ) : null}
              </div>
            </>
          )}
        </div>
      </div>
      <Footer />
      <PaymentCancelModal
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        onConfirm={handleCancelPayment}
      />
    </>
  );
}

export default MyPage;
